import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { RecordForm } from '../components/record-form.js';
import { Card, ErrorBanner, PageHeader } from '../components/ui.js';
import { useEntity } from '../lib/meta.js';

export interface NewSearch {
  /** Values to start the form with, such as the opportunity a survey is booked for. */
  defaults?: Record<string, string | number | boolean>;
}

export function validateNewSearch(search: Record<string, unknown>): NewSearch {
  const raw = search['defaults'];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const defaults: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (
      /^[a-z][a-z0-9_]*$/.test(key) &&
      (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
    )
      defaults[key] = value;
  }
  return Object.keys(defaults).length > 0 ? { defaults } : {};
}

/** Every entity's create screen, drawn from its fields and layout like the record screen. */
export function RecordNew() {
  const { entity: entityId } = useParams({ strict: false }) as { entity: string };
  const { defaults } = useSearch({ strict: false }) as NewSearch;
  const navigate = useNavigate();
  const entity = useEntity(entityId);
  const back = () => void navigate({ to: '/e/$entity', params: { entity: entityId } });
  return (
    <>
      <PageHeader
        eyebrow={
          <Link to="/e/$entity" params={{ entity: entityId }} className="hover:underline">
            {entity.plural}
          </Link>
        }
        title={`New ${entity.label.toLowerCase()}`}
      />
      {entity.access.create ? (
        <Card className="max-w-4xl">
          <RecordForm
            entity={entity}
            defaults={defaults ?? {}}
            submitLabel={`Create ${entity.label.toLowerCase()}`}
            onCancel={back}
            onSaved={(record) =>
              void navigate({
                to: '/e/$entity/$id',
                params: { entity: entityId, id: record.id },
                replace: true,
              })
            }
          />
        </Card>
      ) : (
        <ErrorBanner title="You cannot create these">
          Ask an administrator for a role that may create {entity.plural.toLowerCase()}.
        </ErrorBanner>
      )}
    </>
  );
}
